import React, { useEffect, useState, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import ReactMarkdown from 'react-markdown';
import api from '../services/api';
import VoiceNoteButton from '../components/VoiceNoteButton';

/* ── Category metadata ── */
const CATEGORIES = [
  {
    id: 'A',
    title: 'Internship',
    subtitle: '3rd, 4th and 5th year law students',
    note: 'Most Indian law firms do not offer internships to 1st and 2nd year students. This tool is designed for students ready to apply for real internships.',
    icon: (
      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
        <rect x="2" y="7" width="20" height="14" rx="2" ry="2" /><path d="M16 7V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2" />
      </svg>
    ),
  },
  {
    id: 'B',
    title: 'First Job or PPO',
    subtitle: 'Final year students and fresh graduates',
    note: null,
    icon: (
      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
        <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" /><polyline points="22 4 12 14.01 9 11.01" />
      </svg>
    ),
  },
  {
    id: 'C',
    title: 'Experienced',
    subtitle: 'Lawyers with 2 to 3 years of practice',
    note: null,
    icon: (
      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" />
      </svg>
    ),
  },
];

/* ── CSS ── */
const MI_CSS = `
  .mi-root {
    position: relative;
    min-height: calc(100dvh - var(--nav-h));
    background: #dbeeff;
    overflow: hidden;
    color: #0c2340;
  }
  .mi-root::before {
    content: '';
    position: absolute; inset: 0;
    background:
      radial-gradient(ellipse 80% 50% at 20% 0%, rgba(56,132,255,0.18), transparent 60%),
      radial-gradient(ellipse 60% 50% at 80% 100%, rgba(12,35,64,0.10), transparent 60%),
      linear-gradient(180deg, #e6f3ff 0%, #c9e3ff 100%);
    pointer-events: none;
  }
  .mi-inner {
    position: relative; z-index: 3;
    max-width: 720px; margin: 0 auto;
    padding: 32px 18px 80px;
  }

  /* Back link */
  .mi-back {
    display: inline-flex; align-items: center; gap: 6px;
    font-size: 13px; font-weight: 600; color: #0c2340;
    text-decoration: none; margin-bottom: 20px; opacity: 0.7;
    transition: opacity .2s;
  }
  .mi-back:hover { opacity: 1; }

  /* Header */
  .mi-header { margin-bottom: 28px; }
  .mi-header h1 {
    font-family: 'DM Sans', sans-serif;
    font-size: clamp(26px, 5vw, 36px);
    font-weight: 800; color: #0c2340;
    margin: 0 0 6px;
  }
  .mi-header p {
    font-size: 14px; color: rgba(12,35,64,0.65);
    margin: 0; line-height: 1.5;
  }

  /* Category cards */
  .mi-cats { display: flex; flex-direction: column; gap: 14px; margin-bottom: 24px; }
  .mi-cat {
    display: flex; align-items: flex-start; gap: 16px;
    padding: 22px 20px;
    border-radius: 16px;
    background: linear-gradient(160deg, #ffffff 0%, #eaf4ff 100%);
    border: 2px solid rgba(12,35,64,0.12);
    cursor: pointer;
    transition: border-color .25s, box-shadow .25s, transform .25s;
  }
  .mi-cat:hover {
    border-color: rgba(30,58,138,0.4);
    box-shadow: 0 8px 24px -8px rgba(12,35,64,0.15);
    transform: translateY(-2px);
  }
  .mi-cat.selected {
    border-color: #1e3a8a;
    box-shadow: 0 0 0 3px rgba(30,58,138,0.15), 0 8px 24px -8px rgba(12,35,64,0.2);
  }
  .mi-cat-icon {
    width: 52px; height: 52px; border-radius: 14px;
    background: linear-gradient(135deg, rgba(59,130,246,0.18), rgba(59,130,246,0.04));
    border: 1px solid rgba(12,35,64,0.15);
    display: flex; align-items: center; justify-content: center;
    color: #0c2340; flex-shrink: 0;
  }
  .mi-cat h3 {
    font-family: 'DM Sans', sans-serif;
    font-size: 18px; font-weight: 700; color: #0c2340;
    margin: 0 0 3px;
  }
  .mi-cat p { font-size: 13px; color: rgba(12,35,64,0.6); margin: 0; line-height: 1.5; }
  .mi-cat .mi-cat-note {
    font-size: 12px; color: #92400e;
    background: rgba(251,191,36,0.12);
    border-radius: 8px; padding: 8px 12px;
    margin-top: 8px; line-height: 1.5;
  }

  /* Upload area */
  .mi-upload-zone {
    border: 2px dashed rgba(12,35,64,0.25);
    border-radius: 16px;
    padding: 40px 24px;
    text-align: center;
    background: rgba(255,255,255,0.6);
    cursor: pointer;
    transition: border-color .2s, background .2s;
  }
  .mi-upload-zone:hover, .mi-upload-zone.drag-over {
    border-color: #1e3a8a;
    background: rgba(255,255,255,0.9);
  }
  .mi-upload-zone.has-file {
    border-style: solid;
    border-color: #16a34a;
    background: rgba(255,255,255,0.8);
  }
  .mi-upload-zone p { margin: 6px 0; }
  .mi-file-name {
    display: inline-flex; align-items: center; gap: 8px;
    font-size: 14px; font-weight: 600; color: #0c2340;
  }
  .mi-privacy {
    font-size: 12px; color: rgba(12,35,64,0.5);
    margin-top: 12px;
  }

  /* Chat area */
  .mi-chat {
    display: flex; flex-direction: column;
    gap: 16px;
    margin-bottom: 16px;
  }
  .mi-msg {
    display: flex; gap: 10px;
    max-width: 88%;
    animation: mi-rise 0.4s ease both;
  }
  .mi-msg.user {
    align-self: flex-end;
    flex-direction: row-reverse;
  }
  .mi-msg-avatar {
    width: 32px; height: 32px; border-radius: 50%;
    object-fit: cover; object-position: top center;
    flex-shrink: 0;
    border: 1.5px solid rgba(12,35,64,0.15);
  }
  .mi-msg-avatar.user-ava {
    background: #0c2340; color: #fff;
    display: flex; align-items: center; justify-content: center;
    font-size: 9px; font-weight: 700;
    border: none;
  }
  .mi-msg-bubble {
    padding: 14px 18px;
    border-radius: 4px 16px 16px 16px;
    background: #fff;
    border: 1px solid rgba(12,35,64,0.1);
    box-shadow: 0 2px 8px rgba(12,35,64,0.06);
    font-size: 14px; line-height: 1.65;
    color: #0c2340;
  }
  .mi-msg.user .mi-msg-bubble {
    background: #0c2340; color: #fff;
    border-radius: 16px 16px 4px 16px;
    border-color: transparent;
  }
  .mi-msg-bubble p { margin: 0 0 8px; }
  .mi-msg-bubble p:last-child { margin-bottom: 0; }

  /* Input area */
  .mi-input-area {
    position: sticky; bottom: 0;
    background: linear-gradient(0deg, #dbeeff 60%, transparent);
    padding: 16px 0 24px;
  }
  .mi-input-wrap {
    display: flex; gap: 10px;
    background: #fff;
    border: 1.5px solid rgba(12,35,64,0.18);
    border-radius: 16px;
    padding: 6px;
    box-shadow: 0 4px 20px rgba(12,35,64,0.08);
  }
  .mi-textarea {
    flex: 1; border: none; outline: none;
    padding: 12px 14px;
    font-family: 'DM Sans', sans-serif;
    font-size: 14px; line-height: 1.6;
    color: #0c2340;
    resize: none;
    min-height: 48px;
    max-height: 180px;
    background: transparent;
  }
  .mi-textarea::placeholder { color: rgba(12,35,64,0.35); }
  .mi-send-btn {
    align-self: flex-end;
    width: 44px; height: 44px;
    border-radius: 12px;
    background: #0c2340;
    color: #fff;
    border: none;
    cursor: pointer;
    display: flex; align-items: center; justify-content: center;
    flex-shrink: 0;
    transition: background .2s, opacity .2s;
  }
  .mi-send-btn:disabled { opacity: 0.35; cursor: default; }
  .mi-send-btn:not(:disabled):hover { background: #1e3a8a; }

  /* Progress bar */
  .mi-progress {
    display: flex; align-items: center; gap: 10px;
    margin-bottom: 16px;
    font-size: 12px; font-weight: 600; color: rgba(12,35,64,0.55);
  }
  .mi-progress-bar {
    flex: 1; height: 4px; border-radius: 2px;
    background: rgba(12,35,64,0.1);
    overflow: hidden;
  }
  .mi-progress-fill {
    height: 100%; border-radius: 2px;
    background: linear-gradient(90deg, #1e3a8a, #3b82f6);
    transition: width .5s ease;
  }

  /* Closing note */
  .mi-note-card {
    background: #fff;
    border-radius: 20px;
    padding: 36px 32px;
    border: 1px solid rgba(12,35,64,0.12);
    box-shadow: 0 8px 40px rgba(12,35,64,0.1);
    animation: mi-rise 0.6s ease both;
  }
  .mi-note-card h2 {
    font-family: 'DM Sans', sans-serif;
    font-size: 20px; font-weight: 700; color: #0c2340;
    margin: 0 0 20px;
  }
  .mi-note-body {
    font-size: 15px; line-height: 1.75; color: #1a2f4a;
  }
  .mi-note-body p { margin: 0 0 10px; }
  .mi-note-body p:last-child { margin-bottom: 0; }
  .mi-note-body strong { color: #0c2340; }

  /* Past sessions */
  .mi-past-list { display: flex; flex-direction: column; gap: 12px; }
  .mi-past-item {
    padding: 18px 20px;
    border-radius: 14px;
    background: #fff;
    border: 1px solid rgba(12,35,64,0.12);
    cursor: pointer;
    transition: border-color .2s, box-shadow .2s;
  }
  .mi-past-item:hover {
    border-color: rgba(30,58,138,0.3);
    box-shadow: 0 4px 16px rgba(12,35,64,0.08);
  }
  .mi-past-item h4 { font-size: 15px; font-weight: 700; color: #0c2340; margin: 0 0 4px; }
  .mi-past-item p { font-size: 12px; color: rgba(12,35,64,0.5); margin: 0; }

  /* Buttons */
  .mi-btn {
    display: inline-flex; align-items: center; justify-content: center; gap: 8px;
    padding: 13px 28px;
    border-radius: 12px;
    font-family: 'DM Sans', sans-serif;
    font-size: 14px; font-weight: 700;
    border: none; cursor: pointer;
    transition: background .2s, opacity .2s, transform .15s;
  }
  .mi-btn:active { transform: scale(0.97); }
  .mi-btn-primary { background: #0c2340; color: #fff; }
  .mi-btn-primary:hover { background: #1e3a8a; }
  .mi-btn-primary:disabled { opacity: 0.4; cursor: default; }
  .mi-btn-secondary {
    background: rgba(12,35,64,0.06); color: #0c2340;
    border: 1px solid rgba(12,35,64,0.15);
  }
  .mi-btn-secondary:hover { background: rgba(12,35,64,0.1); }

  /* Cooldown */
  .mi-cooldown {
    background: #fff;
    border-radius: 20px;
    padding: 36px 32px;
    border: 1px solid rgba(12,35,64,0.12);
    box-shadow: 0 8px 40px rgba(12,35,64,0.1);
  }
  .mi-cooldown p {
    font-size: 15px; line-height: 1.75; color: #1a2f4a;
    margin: 0 0 12px;
  }
  .mi-cooldown .sig {
    font-size: 14px; font-weight: 600; color: #0c2340;
    margin-top: 16px;
  }

  /* Typing indicator */
  .mi-typing {
    display: flex; gap: 4px; align-items: center; height: 20px;
    padding: 14px 18px;
    background: #fff;
    border: 1px solid rgba(12,35,64,0.1);
    border-radius: 4px 16px 16px 16px;
    box-shadow: 0 2px 8px rgba(12,35,64,0.06);
  }
  .mi-dot {
    width: 7px; height: 7px; border-radius: 50%;
    background: #3b82f6; opacity: 0.5;
    animation: mi-bounce 1.2s infinite ease-in-out;
  }
  .mi-dot:nth-child(2) { animation-delay: 0.18s; }
  .mi-dot:nth-child(3) { animation-delay: 0.36s; }

  /* Error */
  .mi-error {
    font-size: 13px; color: #b91c1c;
    background: #fef2f2; padding: 10px 14px;
    border-radius: 10px; margin: 12px 0;
  }

  /* Tabs */
  .mi-tabs {
    display: flex; gap: 8px; margin-bottom: 24px;
  }
  .mi-tab {
    padding: 8px 18px; border-radius: 99px;
    font-size: 13px; font-weight: 600;
    border: 1.5px solid rgba(12,35,64,0.15);
    background: rgba(255,255,255,0.5);
    color: rgba(12,35,64,0.6);
    cursor: pointer;
    transition: all .2s;
  }
  .mi-tab.active {
    background: #0c2340; color: #fff;
    border-color: #0c2340;
  }

  /* Upload message */
  .mi-upload-msg {
    background: rgba(255,255,255,0.7);
    border-radius: 16px;
    padding: 24px;
    margin-bottom: 20px;
    border: 1px solid rgba(12,35,64,0.1);
    font-size: 14px; line-height: 1.7; color: #1a2f4a;
  }
  .mi-upload-msg strong { color: #0c2340; }

  @keyframes mi-rise {
    from { opacity: 0; transform: translateY(16px); }
    to   { opacity: 1; transform: translateY(0); }
  }
  @keyframes mi-bounce {
    0%, 80%, 100% { transform: scale(0); }
    40% { transform: scale(1); }
  }
`;

export default function MockInterview() {
  const { user } = useAuth();
  const firstName = user?.name?.split(' ')[0] || '';
  const [step, setStep] = useState('loading'); // loading, cooldown, select, upload, chat, note, history, historyDetail
  const [tab, setTab] = useState('new'); // new, past
  const [category, setCategory] = useState(null);
  const [file, setFile] = useState(null);
  const [sessionId, setSessionId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [progress, setProgress] = useState(null);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [closingNote, setClosingNote] = useState('');
  const [cooldownData, setCooldownData] = useState(null);
  const [pastSessions, setPastSessions] = useState([]);
  const [selectedPast, setSelectedPast] = useState(null);
  const [dragOver, setDragOver] = useState(false);

  const bottomRef = useRef(null);
  const fileInputRef = useRef(null);
  const textareaRef = useRef(null);

  // Inject CSS
  useEffect(() => {
    if (!document.getElementById('mi-style')) {
      const tag = document.createElement('style');
      tag.id = 'mi-style';
      tag.textContent = MI_CSS;
      document.head.appendChild(tag);
    }
  }, []);

  // Scroll to bottom on new messages
  useEffect(() => {
    if (step === 'chat') {
      bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, loading, step]);

  // Auto-resize textarea
  const handleTextareaChange = useCallback((e) => {
    setInput(e.target.value);
    const el = e.target;
    el.style.height = 'auto';
    el.style.height = Math.min(el.scrollHeight, 180) + 'px';
  }, []);

  // Check status on mount
  useEffect(() => { checkStatus(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  async function checkStatus() {
    try {
      const { data } = await api.get('/mock-interview');

      setPastSessions(data.pastSessions || []);

      // Check for resumable session
      if (data.resumable) {
        setSessionId(data.resumable.sessionId);
        setMessages(data.resumable.messages);
        setCategory(data.resumable.category);
        setProgress({
          current: data.resumable.currentAnchorIndex + 1,
          total: data.resumable.totalAnchors,
        });
        setStep('chat');
        return;
      }

      // Check cooling period
      if (!data.canStart && data.cooldownUntil) {
        setCooldownData({
          lastDate: data.lastCompletedAt,
          resumeDate: data.cooldownUntil,
        });
        // If they have past sessions, show the past tab within cooldown view
        if (data.pastSessions?.length > 0) {
          setStep('cooldown');
        } else {
          setStep('cooldown');
        }
        return;
      }

      // Can start fresh
      if (data.pastSessions?.length > 0) {
        setStep('select');
      } else {
        setStep('select');
      }
    } catch (e) {
      setError('Failed to load. Please try again.');
      setStep('select');
    }
  }

  async function startSession() {
    if (!category || !file) return;
    setLoading(true);
    setError('');
    try {
      const formData = new FormData();
      formData.append('category', category);
      formData.append('cv', file);

      const { data } = await api.post('/mock-interview/start', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 60000,
      });

      setSessionId(data.sessionId);
      setMessages(data.messages);
      setProgress(data.progress);
      setStep('chat');
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to start session. Please try again.');
    }
    setLoading(false);
  }

  async function submitAnswer() {
    if (!input.trim() || loading) return;
    const text = input.trim();
    setInput('');
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
    }

    // Optimistically add user message
    setMessages(prev => [...prev, { role: 'user', content: text }]);
    setLoading(true);
    setError('');

    try {
      const { data } = await api.post(`/mock-interview/${sessionId}/answer`, { text }, {
        timeout: 120000, // closing note can take time
      });

      if (data.type === 'closing_note') {
        setClosingNote(data.closingNote);
        setProgress(data.progress);
        setStep('note');
      } else {
        setMessages(prev => [...prev, data.message]);
        setProgress(data.progress);
      }
    } catch (e) {
      setError(e.response?.data?.error || 'Something went wrong. Your answers have been saved. Please try again in a moment.');
    }
    setLoading(false);
  }

  async function viewPastSession(id) {
    setLoading(true);
    try {
      const { data } = await api.get(`/mock-interview/sessions/${id}`);
      setSelectedPast(data);
      setStep('historyDetail');
    } catch (e) {
      setError('Failed to load session.');
    }
    setLoading(false);
  }

  function handleFileSelect(e) {
    const f = e.target?.files?.[0] || e;
    if (!f) return;
    if (f.type !== 'application/pdf') {
      setError('Only PDF files are accepted.');
      return;
    }
    if (f.size > 5 * 1024 * 1024) {
      setError('File too large. Maximum 5 MB.');
      return;
    }
    setError('');
    setFile(f);
  }

  function handleDrop(e) {
    e.preventDefault();
    setDragOver(false);
    const f = e.dataTransfer?.files?.[0];
    if (f) handleFileSelect(f);
  }

  function handleKeyDown(e) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      submitAnswer();
    }
  }

  const fmtDate = (d) => new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' });

  function downloadNote(noteText, date) {
    const dateStr = fmtDate(date || new Date());
    const html = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>Mock Interview Note</title>
<style>
@import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;600;700&display=swap');
body { font-family: 'DM Sans', sans-serif; max-width: 700px; margin: 40px auto; padding: 0 32px; color: #1a2f4a; line-height: 1.8; font-size: 15px; }
.date { font-size: 12px; color: #9ca3af; margin-bottom: 20px; }
.footer { margin-top: 32px; font-size: 13px; color: #9ca3af; font-style: italic; }
h1,h2,h3 { color: #0c2340; }
@media print { body { margin: 20px; } }
</style></head><body>
<p class="date">${dateStr}</p>
${noteText.split('\n').map(l => l.trim() ? '<p>' + l.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>').replace(/\*(.*?)\*/g, '<em>$1</em>') + '</p>' : '').join('\n')}
<p class="footer">www.asknitin.com</p>
</body></html>`;
    const w = window.open('', '_blank');
    w.document.write(html);
    w.document.close();
    setTimeout(() => { w.print(); }, 400);
  }

  // ─── Renders ───

  if (step === 'loading') {
    return (
      <div className="mi-root">
        <div className="mi-inner" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '60vh' }}>
          <div style={{ width: 28, height: 28, borderRadius: '50%', border: '2.5px solid rgba(12,35,64,0.15)', borderTop: '2.5px solid #0c2340', animation: 'spin 0.8s linear infinite' }} />
        </div>
      </div>
    );
  }

  return (
    <div className="mi-root">
      <div className="mi-inner">
        <Link to="/" className="mi-back">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6" /></svg>
          Dashboard
        </Link>

        {/* ── Cooldown ── */}
        {step === 'cooldown' && (
          <>
            <div className="mi-header">
              <h1>Mock Interview with Nitin</h1>
            </div>

            {pastSessions.length > 0 && (
              <div className="mi-tabs">
                <button className={`mi-tab ${tab === 'new' ? 'active' : ''}`} onClick={() => setTab('new')}>New Session</button>
                <button className={`mi-tab ${tab === 'past' ? 'active' : ''}`} onClick={() => setTab('past')}>Past Interviews</button>
              </div>
            )}

            {tab === 'new' ? (
              <div className="mi-cooldown">
                <p>Dear {firstName || 'Student'}, you completed a conversation with me on {fmtDate(cooldownData.lastDate)}. I'd like you to sit with that note for a few more days before we speak again. Come back on {fmtDate(cooldownData.resumeDate)}. And when you do, bring what you've actually worked on — not a new set of answers. The note I wrote is more useful than a new one.</p>
                <p className="sig">— Nitin</p>
              </div>
            ) : renderPastSessions()}
          </>
        )}

        {/* ── Category selection ── */}
        {step === 'select' && (
          <>
            <div className="mi-header">
              <h1>Mock Interview with Nitin</h1>
              <p>A real conversation, a real note, and a real sense of where you stand.</p>
            </div>

            {pastSessions.length > 0 && (
              <div className="mi-tabs">
                <button className={`mi-tab ${tab === 'new' ? 'active' : ''}`} onClick={() => setTab('new')}>New Session</button>
                <button className={`mi-tab ${tab === 'past' ? 'active' : ''}`} onClick={() => setTab('past')}>Past Interviews</button>
              </div>
            )}

            {tab === 'past' ? renderPastSessions() : (
              <>
                <p style={{ fontSize: 13, fontWeight: 700, letterSpacing: 2, textTransform: 'uppercase', color: 'rgba(12,35,64,0.5)', marginBottom: 14 }}>Select your category</p>
                <div className="mi-cats">
                  {CATEGORIES.map(cat => (
                    <div
                      key={cat.id}
                      className={`mi-cat ${category === cat.id ? 'selected' : ''}`}
                      onClick={() => setCategory(cat.id)}
                    >
                      <div className="mi-cat-icon">{cat.icon}</div>
                      <div style={{ flex: 1 }}>
                        <h3>{cat.title}</h3>
                        <p>{cat.subtitle}</p>
                        {cat.note && category === cat.id && (
                          <div className="mi-cat-note">{cat.note}</div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>

                <button
                  className="mi-btn mi-btn-primary"
                  disabled={!category}
                  onClick={() => { setError(''); setStep('upload'); }}
                  style={{ width: '100%' }}
                >
                  Continue
                </button>
              </>
            )}
          </>
        )}

        {/* ── CV Upload ── */}
        {step === 'upload' && (
          <>
            <div className="mi-header">
              <h1>Upload Your CV</h1>
            </div>

            <div className="mi-upload-msg">
              <p><strong>Please upload your CV before we begin. PDF only, maximum 2 pages.</strong></p>
              <p>If you don't have a CV ready, step away for a moment and prepare one using the <Link to="/resume" style={{ color: '#1e3a8a', fontWeight: 600 }}>Resume Builder and Assessment</Link> here at AskNitin. Come back when it's done. An interview without a CV in front of me is not an interview — it's a chat. And I'd rather give you the real thing.</p>
            </div>

            <div
              className={`mi-upload-zone ${file ? 'has-file' : ''} ${dragOver ? 'drag-over' : ''}`}
              onClick={() => fileInputRef.current?.click()}
              onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={handleDrop}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept="application/pdf"
                style={{ display: 'none' }}
                onChange={handleFileSelect}
              />
              {file ? (
                <>
                  <div className="mi-file-name">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#16a34a" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" /><polyline points="22 4 12 14.01 9 11.01" /></svg>
                    {file.name}
                  </div>
                  <p style={{ fontSize: 12, color: 'rgba(12,35,64,0.45)', marginTop: 8 }}>Click to change file</p>
                </>
              ) : (
                <>
                  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="rgba(12,35,64,0.3)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="17 8 12 3 7 8" /><line x1="12" y1="3" x2="12" y2="15" /></svg>
                  <p style={{ fontSize: 14, fontWeight: 600, color: '#0c2340' }}>Drop your CV here or click to browse</p>
                  <p style={{ fontSize: 12, color: 'rgba(12,35,64,0.45)' }}>PDF only, maximum 2 pages, 5 MB</p>
                </>
              )}
            </div>

            <p className="mi-privacy">Your CV is not stored. It is used only for this conversation and deleted immediately after.</p>

            {error && <div className="mi-error">{error}</div>}

            <div style={{ display: 'flex', gap: 10, marginTop: 20 }}>
              <button className="mi-btn mi-btn-secondary" onClick={() => { setStep('select'); setFile(null); setError(''); }}>
                Back
              </button>
              <button
                className="mi-btn mi-btn-primary"
                disabled={!file || loading}
                onClick={startSession}
                style={{ flex: 1 }}
              >
                {loading ? 'Starting...' : 'Begin Interview'}
              </button>
            </div>
          </>
        )}

        {/* ── Chat ── */}
        {step === 'chat' && (
          <>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '4px' }}>
              {progress && (
                <div className="mi-progress" style={{ flex: 1, marginBottom: 0 }}>
                  <span>Question {progress.current} of {progress.total}</span>
                  <div className="mi-progress-bar">
                    <div className="mi-progress-fill" style={{ width: `${(progress.current / progress.total) * 100}%` }} />
                  </div>
                </div>
              )}
              <Link to="/" className="mi-btn mi-btn-secondary" style={{ padding: '6px 14px', fontSize: 12, marginLeft: 10, textDecoration: 'none', whiteSpace: 'nowrap' }}>
                Save and Exit
              </Link>
            </div>

            <div className="mi-chat">
              {messages.map((msg, i) => (
                <div key={i} className={`mi-msg ${msg.role}`}>
                  {msg.role === 'assistant' ? (
                    <img src="/nitin-potdar-profile.png" alt="Nitin" className="mi-msg-avatar" />
                  ) : (
                    <div className="mi-msg-avatar user-ava">You</div>
                  )}
                  <div className="mi-msg-bubble">
                    <ReactMarkdown
                      components={{
                        p: ({ children }) => <p>{children}</p>,
                        strong: ({ children }) => <strong style={{ fontWeight: 700 }}>{children}</strong>,
                        em: ({ children }) => <em>{children}</em>,
                      }}
                    >
                      {msg.content}
                    </ReactMarkdown>
                  </div>
                </div>
              ))}

              {loading && (
                <div className="mi-msg">
                  <img src="/nitin-potdar-profile.png" alt="Nitin" className="mi-msg-avatar" />
                  <div className="mi-typing">
                    <div className="mi-dot" />
                    <div className="mi-dot" />
                    <div className="mi-dot" />
                  </div>
                </div>
              )}
              <div ref={bottomRef} />
            </div>

            {error && <div className="mi-error">{error}</div>}

            <div className="mi-input-area">
              <div className="mi-input-wrap">
                <textarea
                  ref={textareaRef}
                  className="mi-textarea"
                  value={input}
                  onChange={handleTextareaChange}
                  onKeyDown={handleKeyDown}
                  placeholder="Write your answer here... (Shift+Enter for new line)"
                  disabled={loading}
                  rows={2}
                />
                <VoiceNoteButton
                  disabled={loading}
                  onTranscript={(text) => {
                    setInput(prev => prev ? prev.trimEnd() + ' ' + text : text);
                    if (textareaRef.current) {
                      setTimeout(() => {
                        textareaRef.current.style.height = 'auto';
                        textareaRef.current.style.height = Math.min(textareaRef.current.scrollHeight, 180) + 'px';
                      }, 0);
                    }
                  }}
                />
                <button
                  className="mi-send-btn"
                  disabled={!input.trim() || loading}
                  onClick={submitAnswer}
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="22" y1="2" x2="11" y2="13" /><polygon points="22 2 15 22 11 13 2 9 22 2" /></svg>
                </button>
              </div>
            </div>
          </>
        )}

        {/* ── Closing Note ── */}
        {step === 'note' && (
          <>
            <div className="mi-header">
              <h1>Your Personal Note from Nitin</h1>
            </div>
            <div className="mi-note-card">
              <p style={{ fontSize: 12, color: 'rgba(12,35,64,0.45)', margin: '0 0 14px' }}>{fmtDate(new Date())}</p>
              <div className="mi-note-body">
                <ReactMarkdown
                  components={{
                    p: ({ children }) => <p>{children}</p>,
                    strong: ({ children }) => <strong>{children}</strong>,
                    em: ({ children }) => <em>{children}</em>,
                    h1: ({ children }) => <p style={{ fontWeight: 700, fontSize: 16, marginTop: 16 }}>{children}</p>,
                    h2: ({ children }) => <p style={{ fontWeight: 700, fontSize: 15, marginTop: 14 }}>{children}</p>,
                    h3: ({ children }) => <p style={{ fontWeight: 700, fontSize: 15, marginTop: 12 }}>{children}</p>,
                    ul: ({ children }) => <ul style={{ paddingLeft: 18, margin: '8px 0' }}>{children}</ul>,
                    ol: ({ children }) => <ol style={{ paddingLeft: 18, margin: '8px 0' }}>{children}</ol>,
                    li: ({ children }) => <li style={{ lineHeight: 1.7, marginBottom: 4 }}>{children}</li>,
                  }}
                >
                  {closingNote}
                </ReactMarkdown>
              </div>
              <p style={{ fontSize: 13, color: 'rgba(12,35,64,0.4)', margin: '16px 0 0', fontStyle: 'italic' }}>www.asknitin.com</p>
              <button
                className="mi-btn mi-btn-primary"
                onClick={() => downloadNote(closingNote, new Date())}
                style={{ width: '100%', marginTop: 20 }}
              >
                Download Note
              </button>
            </div>

            <div style={{ display: 'flex', gap: 10, marginTop: 24 }}>
              <Link to="/" className="mi-btn mi-btn-secondary" style={{ textDecoration: 'none' }}>
                Dashboard
              </Link>
              <button
                className="mi-btn mi-btn-primary"
                onClick={() => { setTab('past'); checkStatus().then(() => setStep('select')); }}
                style={{ flex: 1 }}
              >
                View Closing Note
              </button>
            </div>
          </>
        )}

        {/* ── History Detail ── */}
        {step === 'historyDetail' && selectedPast && (
          <>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20 }}>
              <button
                className="mi-btn mi-btn-secondary"
                onClick={() => setStep(cooldownData ? 'cooldown' : 'select')}
                style={{ padding: '8px 16px', fontSize: 13 }}
              >
                Back
              </button>
              <div>
                <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: '#0c2340' }}>
                  {selectedPast.categoryLabel}
                </h3>
                <p style={{ margin: 0, fontSize: 12, color: 'rgba(12,35,64,0.5)' }}>
                  {fmtDate(selectedPast.completedAt)}
                </p>
              </div>
            </div>

            {/* Conversation transcript */}
            <p style={{ fontSize: 11, fontWeight: 700, letterSpacing: 2, textTransform: 'uppercase', color: 'rgba(12,35,64,0.4)', marginBottom: 12 }}>Conversation</p>
            <div className="mi-chat" style={{ marginBottom: 28 }}>
              {selectedPast.messages.map((msg, i) => (
                <div key={i} className={`mi-msg ${msg.role}`}>
                  {msg.role === 'assistant' ? (
                    <img src="/nitin-potdar-profile.png" alt="Nitin" className="mi-msg-avatar" />
                  ) : (
                    <div className="mi-msg-avatar user-ava">You</div>
                  )}
                  <div className="mi-msg-bubble">
                    <ReactMarkdown components={{
                      p: ({ children }) => <p>{children}</p>,
                      strong: ({ children }) => <strong style={{ fontWeight: 700 }}>{children}</strong>,
                    }}>
                      {msg.content}
                    </ReactMarkdown>
                  </div>
                </div>
              ))}
            </div>

            {/* Closing note */}
            <p style={{ fontSize: 11, fontWeight: 700, letterSpacing: 2, textTransform: 'uppercase', color: 'rgba(12,35,64,0.4)', marginBottom: 12 }}>Closing Note</p>
            <div className="mi-note-card">
              <p style={{ fontSize: 12, color: 'rgba(12,35,64,0.45)', margin: '0 0 14px' }}>{fmtDate(selectedPast.completedAt)}</p>
              <div className="mi-note-body">
                <ReactMarkdown components={{
                  p: ({ children }) => <p>{children}</p>,
                  strong: ({ children }) => <strong>{children}</strong>,
                  em: ({ children }) => <em>{children}</em>,
                }}>
                  {selectedPast.closingNote}
                </ReactMarkdown>
              </div>
              <p style={{ fontSize: 13, color: 'rgba(12,35,64,0.4)', margin: '16px 0 0', fontStyle: 'italic' }}>www.asknitin.com</p>
              <button
                className="mi-btn mi-btn-primary"
                onClick={() => downloadNote(selectedPast.closingNote, selectedPast.completedAt)}
                style={{ width: '100%', marginTop: 20 }}
              >
                Download Note
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );

  function renderPastSessions() {
    if (pastSessions.length === 0) {
      return <p style={{ fontSize: 14, color: 'rgba(12,35,64,0.5)', textAlign: 'center', padding: '40px 0' }}>No past sessions yet.</p>;
    }
    return (
      <div className="mi-past-list">
        {pastSessions.map(s => (
          <div key={s._id} className="mi-past-item" onClick={() => viewPastSession(s._id)}>
            <h4>{s.categoryLabel}</h4>
            <p>{fmtDate(s.completedAt)}</p>
          </div>
        ))}
      </div>
    );
  }
}
